//@ts-expect-error
import { decode } from "he";
import { useLocale, useTranslations } from "next-intl";
import { ArrowTrendingUpIcon } from "@heroicons/react/20/solid";
import { Pricesection } from "@types";
import { Badge, Card, Text } from "@components/theming";
import { formatPrice } from "@utils/helpers";
import EventToolTip from "./eventToolTip";

function getPricingTense(availability?: {
  startDate: string | null;
  endDate: string | null;
  formatted: string | null;
  local: {
    startDate: string | null;
    endDate: string | null;
    now: string;
  };
  offset: {
    start: number | null;
    end: number | null;
  };
}) {
  if (!availability) return "present";
  if (
    availability.local.startDate &&
    availability.local.endDate &&
    availability.local.now >= availability.local.startDate &&
    availability.local.now <= availability.local.endDate
  ) {
    return "present";
  }
  if (
    availability.local.startDate &&
    !availability.local.endDate &&
    availability.local.now >= availability.local.startDate
  ) {
    return "present";
  }
  if (
    availability.local.endDate &&
    !availability.local.startDate &&
    availability.local.now <= availability.local.endDate
  ) {
    return "present";
  }
  if (
    availability.local.startDate &&
    availability.local.now < availability.local.startDate
  ) {
    return "future";
  }
  if (
    availability.local.endDate &&
    availability.local.now > availability.local.endDate
  ) {
    return "past";
  }
  return "present";
}

interface Props {
  pricesection: Pricesection;
  priceschemename: string;
  currency: string;
}

interface CombinedArray {
  id: number;
  name: string;
  description: string;
  availability?: {
    startDate: string | null;
    endDate: string | null;
    formatted: string | null;
    local: {
      startDate: string | null;
      endDate: string | null;
      now: string;
    };
    offset: {
      start: number | null;
      end: number | null;
    };
    showInPricingInfo: boolean;
    customMessage?: string | null;
  };
  price: number;
  isDynamic?: boolean;
}

const PriceInformation = ({
  pricesection,
  priceschemename,
  currency,
}: Props) => {
  const locale = useLocale();
  const t = useTranslations("EventPage.priceinformation");

  const priceages = pricesection.priceages
    ?.filter(
      (pa) =>
        (pa.type === "standard" || pa.type === "extra") &&
        pa.active &&
        pa.display
    )
    .map((priceage) => ({
      id: priceage.id,
      name: priceage.name,
      description: priceage.description,
      availability: priceage.availability,
      display: priceage.display,
      price: priceage.price.price,
      isDynamic: priceage.price.type.id === 2,
      order: priceage.order,
    }))
    .sort((a, b) => a.order - b.order);

  const manualprices = pricesection.manualprices
    ?.filter((manualprice) => manualprice.active)
    .map((manualprice) => ({
      id: manualprice.id,
      name: manualprice.name,
      description: manualprice.description,
      price: manualprice.price,
      order: manualprice.order,
    }))
    .sort((a, b) => a.order - b.order);

  const combinedArrays: CombinedArray[] = [
    ...(priceages ?? []),
    ...(manualprices ?? []),
  ];

  if (!pricesection.priceages && !pricesection.manualprices) return null;
  if (combinedArrays.length <= 0) return null;

  return (
    <Card
      key={pricesection.id}
      size="2"
      shopsiteClasses="bg-indigo-100 rounded-2xl p-3"
    >
      <div className="flex flex-col gap-2">
        {priceschemename.trim() ||
        pricesection.name.trim() ||
        pricesection.description ? (
          <div className="shrink-0 flex-1 flex flex-col">
            <Text
              as="span"
              size="5"
              weight="bold"
              shopsiteClasses="text-base text-indigo-600 font-bold"
            >
              {priceschemename.trim()}
              {priceschemename && pricesection.name.trim() ? " - " : ""}
              {pricesection.name.trim()}
            </Text>
            {pricesection.description && (
              <Text
                as="span"
                color="gray"
                shopsiteClasses="text-sm text-gray-500"
              >
                {decode(pricesection.description)}
              </Text>
            )}
          </div>
        ) : (
          <></>
        )}
        {combinedArrays?.map((price) => (
          <div key={price.id} className="flex flex-col gap-1 w-full">
            <div className="flex justify-between items-center gap-2">
              <Text
                as="p"
                weight="medium"
                shopsiteClasses="text-indigo-600 font-normal text-sm"
              >
                {price.name}
              </Text>
              <Text
                as="span"
                shopsiteClasses="text-indigo-600 font-normal text-sm"
              >
                {formatPrice(locale, currency, price.price)}
              </Text>
            </div>
            {price.description && (
              <Text
                as="p"
                color="gray"
                size="2"
                shopsiteClasses="text-sm leading-5 text-gray-500"
              >
                {decode(price.description)}
              </Text>
            )}
            <div className="flex items-center flex-wrap gap-1">
              {price.isDynamic ? (
                <Badge
                  sharedClasses="w-fit gap-1 text-sm"
                  color="crimson"
                  shopsiteClasses="rounded-md bg-iticket text-white"
                >
                  <ArrowTrendingUpIcon className="h-4 w-4" />
                  FLEXi
                </Badge>
              ) : (
                <></>
              )}
              {!price.availability?.showInPricingInfo ? (
                <></>
              ) : price.availability?.customMessage ? (
                <Badge
                  sharedClasses="w-fit gap-1 text-sm"
                  color="amber"
                  shopsiteClasses="!rounded-md font-medium"
                >
                  {price.availability.customMessage}
                </Badge>
              ) : price.availability &&
                getPricingTense(price.availability) !== "present" ? (
                <Badge
                  sharedClasses="w-fit gap-1 text-sm"
                  color="amber"
                  shopsiteClasses="!rounded-md font-medium"
                >
                  {getPricingTense(price.availability) === "past"
                    ? t("nolongeravailable")
                    : t("notyetavailable")}
                  <EventToolTip
                    title={
                      getPricingTense(price.availability) === "past"
                        ? t("nolongeravailable")
                        : t("notyetavailable")
                    }
                    description={
                      getPricingTense(price.availability) === "past"
                        ? t("availablefrom", {
                            date: price.availability.formatted ?? "",
                            tense: "past",
                            preposition:
                              !price.availability.startDate &&
                              price.availability.endDate
                                ? "until"
                                : "from",
                          })
                        : t("availablefrom", {
                            date: price.availability.formatted ?? "",
                            tense: "future",
                            preposition:
                              price.availability.startDate &&
                              !price.availability.endDate
                                ? "from"
                                : "between",
                          })
                    }
                    isWarning
                  />
                </Badge>
              ) : price.availability &&
                (price.availability.offset.start ||
                  price.availability.offset.end) ? (
                <Badge
                  sharedClasses="w-fit gap-1 text-sm"
                  color="amber"
                  shopsiteClasses="!rounded-md font-medium"
                >
                  {t("limitedavailability")}
                  <EventToolTip
                    title={t("limitedavailability")}
                    description={
                      price.availability.offset.start === 999 &&
                      price.availability.offset.end
                        ? t("offsetavailableupto", {
                            offset: price.availability.offset.end,
                          })
                        : price.availability.offset.start &&
                          price.availability.offset.end
                        ? t("offsetavailablebetween", {
                            offsetstart: price.availability.offset.start,
                            offsetend: price.availability.offset.end,
                          })
                        : t("offsetavailable", {
                            offset:
                              price.availability.offset.start ??
                              price.availability.offset.end ??
                              "",
                          })
                    }
                    isWarning
                  />
                </Badge>
              ) : (
                <></>
              )}
            </div>
          </div>
        ))}
      </div>
    </Card>
  );
};

export default PriceInformation;
